import {
  AMOUNT_MESSAGE,
  HEALTH_INSURANCE_MESSAGE,
  MONTH_MESSAGE,
  formatAmountInput,
  parseAmountInput,
  selfDisclosureAssetsStepSchema,
  selfDisclosureExpensesStepSchema,
  selfDisclosureIncomeStepSchema,
  selfDisclosurePersonStepSchema,
  type SelfDisclosureFormStep,
} from '@brimo/shared';
import type { z } from 'zod';
import type {
  AssetsStepValues,
  ExpensesStepValues,
  IncomeStepValues,
  PersonStepValues,
  PublicState,
} from '../api';

/**
 * Form state of the public form (strings as typed) and its conversion into the step bodies of
 * `@brimo/shared` (cents, `YYYY-MM` keys), validated with the shared zod schemas so that the field
 * errors of Selbstauskunft 04 appear before the server sees the step.
 */

export interface PersonForm {
  firstName: string;
  lastName: string;
  bornOn: string;
  maritalStatus: string;
  children: string;
  nationality: string;
  street: string;
  postalCode: string;
  city: string;
  phone: string;
  email: string;
}

export interface IncomeForm {
  employment: IncomeStepValues['employment'];
  employer: string;
  employedSince: string;
  contract: 'permanent' | 'fixed_term' | '';
  fixedTermUntil: string;
  netIncome: string;
  salariesPerYear: string;
  specialPayments: string;
  otherIncome: string;
  otherIncomeType: string;
  rentalIncome: string;
}

export interface ExpensesForm {
  housingSituation: ExpensesStepValues['housingSituation'];
  grossRent: string;
  rentContinues: 'yes' | 'no';
  loans: string;
  loansRemainingTerm: string;
  alimony: string;
  privateHealthInsurance: string;
}

export interface AssetsForm {
  equity: string;
  securities: string;
  other: string;
}

export interface FormState {
  person: PersonForm;
  income: IncomeForm;
  expenses: ExpensesForm;
  assets: AssetsForm;
}

export type FieldErrors = Record<string, string>;

/** „04/2023" → `2023-04`; empty → null; anything else → undefined (invalid). */
export function toMonthKey(text: string): string | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const match = /^(0[1-9]|1[0-2])\s*\/\s*(\d{4})$/.exec(trimmed);
  if (!match) return undefined;
  return `${match[2]}-${match[1]}`;
}

/** `2023-04` → „04/2023". */
export function fromMonthKey(key: string | null): string {
  if (!key) return '';
  const match = /^(\d{4})-(\d{2})/.exec(key);
  return match ? `${match[2]}/${match[1]}` : key;
}

/** Cents → the field text („4.980" for whole euros, „412,50" otherwise). */
export function amountText(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  const formatted = formatAmountInput(cents);
  return formatted.endsWith(',00') ? formatted.slice(0, -3) : formatted;
}

/** Field text → cents (null when empty); `undefined` when the text is not an amount. */
export function amountCents(text: string): number | null | undefined {
  if (text.trim() === '') return null;
  const cents = parseAmountInput(text);
  return cents === null ? undefined : cents;
}

/**
 * The form's start values: a saved step shows its saved values, an unsaved one the prefill from the contact
 * (`state.prefill`), else the defaults. The prefill never marks a step as saved — `stepDiffers` and the
 * step states compare with `state.steps` only, so a prefilled step is still saved on „Weiter".
 */
export function initialForm(state: PublicState): FormState {
  const contact = state.contact;
  const person = state.steps.person ?? state.prefill.person;
  const income = state.steps.income ?? state.prefill.income;
  const expenses = state.steps.expenses ?? state.prefill.expenses;
  const assets = state.steps.assets ?? state.prefill.assets;
  return {
    person: {
      firstName: person.firstName || contact.firstName,
      lastName: person.lastName || contact.lastName,
      bornOn: person.bornOn ?? contact.bornOn ?? '',
      maritalStatus: person.maritalStatus ?? '',
      children: person.children === null ? '' : String(person.children),
      nationality: person.nationality ?? '',
      street: person.street ?? contact.street ?? '',
      postalCode: person.postalCode ?? contact.postalCode ?? '',
      city: person.city ?? contact.city ?? '',
      phone: person.phone ?? contact.phone ?? '',
      email: person.email ?? contact.email ?? '',
    },
    income: {
      employment: income.employment ?? 'employed',
      employer: income.employer ?? '',
      employedSince: fromMonthKey(income.employedSinceOn),
      contract: income.contract ?? '',
      fixedTermUntil: fromMonthKey(income.fixedTermUntilOn),
      netIncome: amountText(income.netIncomeCents),
      salariesPerYear: income.salariesPerYear === null ? '' : String(income.salariesPerYear),
      specialPayments: amountText(income.specialPaymentsPaCents),
      otherIncome: amountText(income.otherIncomeCents),
      otherIncomeType: income.otherIncomeType ?? '',
      rentalIncome: amountText(income.rentalIncomeCents),
    },
    expenses: {
      housingSituation: expenses.housingSituation ?? 'rent',
      grossRent: amountText(expenses.grossRentCents),
      rentContinues: expenses.rentContinues === false ? 'no' : 'yes',
      loans: amountText(expenses.loansCents),
      loansRemainingTerm: fromMonthKey(expenses.loansRemainingTerm),
      alimony: amountText(expenses.alimonyCents),
      privateHealthInsurance: amountText(expenses.privateHealthInsuranceCents),
    },
    assets: {
      equity: amountText(assets.equityCents),
      securities: amountText(assets.securitiesCents),
      other: amountText(assets.otherCents),
    },
  };
}

function issuesToErrors(issues: ReadonlyArray<{ path: PropertyKey[]; message: string }>): FieldErrors {
  const errors: FieldErrors = {};
  for (const issue of issues) {
    const key = String(issue.path[0] ?? '');
    if (key && errors[key] === undefined) errors[key] = issue.message;
  }
  return errors;
}

export interface Built<T> {
  body: T | null;
  errors: FieldErrors;
}

function nullable(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

export function buildPerson(form: PersonForm): Built<z.infer<typeof selfDisclosurePersonStepSchema>> {
  const errors: FieldErrors = {};
  const children = form.children.trim() === '' ? null : Number(form.children);
  if (children !== null && !Number.isInteger(children))
    errors['children'] = 'Bitte geben Sie die Anzahl Ihrer Kinder an.';
  const candidate = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    bornOn: nullable(form.bornOn),
    maritalStatus: nullable(form.maritalStatus),
    children: Number.isInteger(children) ? children : null,
    nationality: nullable(form.nationality),
    street: nullable(form.street),
    postalCode: nullable(form.postalCode),
    city: nullable(form.city),
    phone: nullable(form.phone),
    email: nullable(form.email),
  };
  const parsed = selfDisclosurePersonStepSchema.safeParse(candidate);
  if (!parsed.success) Object.assign(errors, issuesToErrors(parsed.error.issues));
  return { body: Object.keys(errors).length === 0 && parsed.success ? parsed.data : null, errors };
}

export function buildIncome(form: IncomeForm): Built<z.infer<typeof selfDisclosureIncomeStepSchema>> {
  const errors: FieldErrors = {};
  const employedSince = toMonthKey(form.employedSince);
  if (employedSince === undefined) errors['employedSinceOn'] = MONTH_MESSAGE;
  const fixedTermUntil = toMonthKey(form.fixedTermUntil);
  if (fixedTermUntil === undefined) errors['fixedTermUntilOn'] = MONTH_MESSAGE;
  const netIncome = amountCents(form.netIncome);
  if (netIncome === undefined) errors['netIncomeCents'] = AMOUNT_MESSAGE;
  const specialPayments = amountCents(form.specialPayments);
  if (specialPayments === undefined) errors['specialPaymentsPaCents'] = AMOUNT_MESSAGE;
  const otherIncome = amountCents(form.otherIncome);
  if (otherIncome === undefined) errors['otherIncomeCents'] = AMOUNT_MESSAGE;
  const rentalIncome = amountCents(form.rentalIncome);
  if (rentalIncome === undefined) errors['rentalIncomeCents'] = AMOUNT_MESSAGE;
  const candidate = {
    employment: form.employment,
    employer: nullable(form.employer),
    employedSinceOn: employedSince ?? null,
    contract: form.contract === '' ? null : form.contract,
    fixedTermUntilOn: form.contract === 'fixed_term' ? (fixedTermUntil ?? null) : null,
    netIncomeCents: netIncome ?? 0,
    salariesPerYear: form.salariesPerYear === '' ? null : Number(form.salariesPerYear),
    specialPaymentsPaCents: specialPayments ?? null,
    otherIncomeCents: otherIncome ?? null,
    otherIncomeType: nullable(form.otherIncomeType),
    rentalIncomeCents: rentalIncome ?? null,
  };
  const parsed = selfDisclosureIncomeStepSchema.safeParse(candidate);
  if (!parsed.success) {
    for (const [key, message] of Object.entries(issuesToErrors(parsed.error.issues))) {
      if (errors[key] === undefined) errors[key] = message;
    }
  }
  return { body: Object.keys(errors).length === 0 && parsed.success ? parsed.data : null, errors };
}

export function buildExpenses(form: ExpensesForm): Built<z.infer<typeof selfDisclosureExpensesStepSchema>> {
  const errors: FieldErrors = {};
  const grossRent = amountCents(form.grossRent);
  if (grossRent === undefined) errors['grossRentCents'] = AMOUNT_MESSAGE;
  const loans = amountCents(form.loans);
  if (loans === undefined) errors['loansCents'] = AMOUNT_MESSAGE;
  const term = toMonthKey(form.loansRemainingTerm);
  if (term === undefined) errors['loansRemainingTerm'] = MONTH_MESSAGE;
  const alimony = amountCents(form.alimony);
  if (alimony === undefined) errors['alimonyCents'] = AMOUNT_MESSAGE;
  const health = amountCents(form.privateHealthInsurance);
  if (health === undefined) errors['privateHealthInsuranceCents'] = HEALTH_INSURANCE_MESSAGE;
  const candidate = {
    housingSituation: form.housingSituation,
    grossRentCents: grossRent ?? (form.housingSituation === 'rent' ? 0 : null),
    rentContinues: form.rentContinues === 'yes',
    loansCents: loans ?? null,
    loansRemainingTerm: term ?? null,
    alimonyCents: alimony ?? null,
    privateHealthInsuranceCents: health ?? null,
    leasingCents: null,
  };
  const parsed = selfDisclosureExpensesStepSchema.safeParse(candidate);
  if (!parsed.success) {
    for (const [key, message] of Object.entries(issuesToErrors(parsed.error.issues))) {
      if (errors[key] === undefined) errors[key] = message;
    }
  }
  return { body: Object.keys(errors).length === 0 && parsed.success ? parsed.data : null, errors };
}

export function buildAssets(form: AssetsForm): Built<z.infer<typeof selfDisclosureAssetsStepSchema>> {
  const errors: FieldErrors = {};
  const equity = amountCents(form.equity);
  if (equity === undefined) errors['equityCents'] = AMOUNT_MESSAGE;
  const securities = amountCents(form.securities);
  if (securities === undefined) errors['securitiesCents'] = AMOUNT_MESSAGE;
  const other = amountCents(form.other);
  if (other === undefined) errors['otherCents'] = AMOUNT_MESSAGE;
  const candidate = {
    equityCents: equity ?? null,
    securitiesCents: securities ?? null,
    otherCents: other ?? null,
  };
  const parsed = selfDisclosureAssetsStepSchema.safeParse(candidate);
  if (!parsed.success) {
    for (const [key, message] of Object.entries(issuesToErrors(parsed.error.issues))) {
      if (errors[key] === undefined) errors[key] = message;
    }
  }
  return { body: Object.keys(errors).length === 0 && parsed.success ? parsed.data : null, errors };
}

export function buildStep(step: SelfDisclosureFormStep, form: FormState): Built<unknown> {
  switch (step) {
    case 'person':
      return buildPerson(form.person);
    case 'income':
      return buildIncome(form.income);
    case 'expenses':
      return buildExpenses(form.expenses);
    case 'assets':
      return buildAssets(form.assets);
  }
}

/** Whether the saved step (from the server) differs from what the form would send now. */
export function stepDiffers(step: SelfDisclosureFormStep, form: FormState, state: PublicState): boolean {
  const built = buildStep(step, form);
  if (built.body === null) return true;
  const saved: PersonStepValues | IncomeStepValues | ExpensesStepValues | AssetsStepValues | null =
    state.steps[step];
  if (saved === null) return true;
  const sent = built.body as Record<string, unknown>;
  const current = saved as unknown as Record<string, unknown>;
  for (const [key, value] of Object.entries(sent)) {
    const stored = current[key] ?? null;
    if ((value ?? null) !== stored) return true;
  }
  return false;
}
